import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import InputMask from 'react-input-mask'

import { countriesAuth } from 'sections/LKSection/modules/UserModule/data'
import { UserModuleKeys } from 'sections/LKSection/modules/UserModule/types'
import { Margin } from 'components/ui'
import { ReactSelect } from 'components/ui/Select/ReactSelect'
import { Flex, Input, Text } from 'components/uiKit'
import { BaseOption, Country } from 'shared/types/types'

import { masks, phonePrefix } from './data'

import s from './CountryPhoneInput.module.scss'

export interface CountryPhoneInputProps {
  phonePrefix: string
  phone: string
  country: Country
  disableCountry?: boolean
  setPhone: (value: string) => void
  setSelectedCountry: (value?: CountryItem) => void
}

export type CountryItem = {
  label: string
  value: string
  country: Country
}

export const getPhoneWithoutPrefix = (country: Country, phone?: string): string => {
  if (!phone) {
    return ''
  }
  const prefix = phonePrefix[country]
  return phone.startsWith(prefix) ? phone.slice(prefix.length) : phone
}

const phoneAMlength = 8

export function CountryPhoneInput({
  phone,
  country,
  setPhone,
  setSelectedCountry,
  disableCountry = false,
}: CountryPhoneInputProps) {
  const { t } = useTranslation()
  const phoneAM = phone.slice(0, phoneAMlength)
  const getKey = (key: string) => `${UserModuleKeys.countryPhoneInput}.${key}`

  // TODO Понять зачем тут обратотка для армении
  useEffect(() => {
    if (country === Country.AM) {
      setPhone(phoneAM)
    }
  }, [country])

  const localizedCountries = useMemo(() => {
    return countriesAuth.map((country) => ({
      ...country,
      label: t(getSharedKey(country.value.label)),
    }))
  }, [t])

  const localizedCountry = (value: BaseOption<CountryItem>) => {
    return {
      ...value,
      label: t(getSharedKey(value.label)),
    }
  }

  function getSharedKey(key: string) {
    return `${UserModuleKeys.countries}.${key}`
  }

  function handlePaste(event: React.ClipboardEvent<HTMLInputElement>) {
    event.preventDefault()
    let pasteData = event.clipboardData.getData('Text').replace(/\D/g, '')

    const countryPrefix = phonePrefix[country]

    if (pasteData.length === 11 && pasteData.startsWith('8')) {
      pasteData = countryPrefix + pasteData.slice(1)
    }

    if (!pasteData.startsWith(countryPrefix)) {
      pasteData = countryPrefix + pasteData
    }
    const formattedNumber = getPhoneWithoutPrefix(country, pasteData)
    setPhone(formattedNumber)
  }

  return (
    <Flex vertical>
      <ReactSelect
        placeholder={t(getKey('Страна'))}
        options={localizedCountries}
        value={localizedCountry(countriesAuth.find((value) => value.value.country === country) ?? countriesAuth[0])}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        onChange={(item) => setSelectedCountry(item.value as CountryItem)}
        isDisabled={disableCountry}
      />
      <Margin mt={20}>
        <Flex>
          <Flex vertical>
            <Text>{t(getKey('Код'))}</Text>
            <Input
              className={s.codeInput}
              disabled
              value={`+${
                country
                  ? countriesAuth.find((value) => value.value.country === country)?.value.value
                  : countriesAuth[0].value.value
              }`}
            />
          </Flex>
          <Flex vertical flex={1}>
            <Text>{t(getKey('Телефон'))}</Text>
            <InputMask
              className={s.phoneInput}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onPaste={handlePaste}
              mask={masks[country]}
              placeholder={masks[country]}
              type="tel"
            />
          </Flex>
        </Flex>
      </Margin>
    </Flex>
  )
}
